import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { createContext, ReactNode, useEffect } from "react";
import { ENV_VARS, getCurrentEnv, isProduction } from "../../../config/env";
import { firebaseAuth } from "../../../config/firebase";
import { getSelectedOrganization } from "../../../helpers/localStorage";
import iframeStore from "../../../pages/IframeNew/iframe-store";
import { isMigrationIframe } from "../../../utils/migrationIframe.ts";

interface PostHogContextType {
	posthog: typeof posthog;
}

const PostHogContext = createContext<PostHogContextType | null>(null);

interface PostHogProviderProps {
	children: ReactNode;
}

export const PostHogProvider = ({ children }: PostHogProviderProps) => {
	useEffect(() => {
		const initPostHog = () => {
			if (isMigrationIframe()) {
				// disable posthog inside the iframe
				console.log("PostHogProvider: Disabling PostHog inside the migration iframe");
				return;
			}
			if (!ENV_VARS.VITE_PUBLIC_POSTHOG_KEY) {
				console.log("PostHogProvider: No API key found, skipping initialization");
				return;
			}

			// Only initialize PostHog in the production environment
			if (!isProduction()) {
				console.log(`PostHogProvider: Skipping initialization in ${getCurrentEnv()} environment`);
				return;
			}

			// Only initialize PostHog in a non-iframe environment
			if (iframeStore.isIframe()) {
				console.log(`PostHogProvider: Skipping initialization in iframe environment`);
				return;
			}

			try {
				posthog.init(ENV_VARS.VITE_PUBLIC_POSTHOG_KEY, {
					api_host: "https://d.bria.ai",
					ui_host: "https://eu.posthog.com",
					person_profiles: "identified_only",
					capture_pageview: false,
					capture_pageleave: true,
					disable_session_recording: false,
					capture_performance: true,
					loaded: (_posthog: any) => {
						console.log(`PostHogProvider: initialized in ${getCurrentEnv()} environment`);
					},
					// Filter out Bria org users to maximize free credits
					before_send: (event) => {
						if (!event) return null;

						const BRIA_ORG_DOMAINS = ["@bria.ai"];
						const isBriaUser = (email: string | null | undefined): boolean => {
							if (!email) return false;
							return BRIA_ORG_DOMAINS.some((domain) => email.toLowerCase().includes(domain));
						};

						// Check Firebase user email directly (most reliable)
						const currentEmail = firebaseAuth.currentUser?.email;

						// Also check various places where email might be stored in the event
						const eventEmail =
							event.properties?.email || event.properties?.$set?.email || event.$set?.email;

						if (isBriaUser(currentEmail) || isBriaUser(eventEmail)) {
							console.log("PostHog: User is internal, skipping event");
							return null;
						}

						const selectedOrg = getSelectedOrganization();
						const personProperties = {
							email: currentEmail || event.properties.distinct_id,
							uid: firebaseAuth.currentUser?.uid,
							name: firebaseAuth.currentUser?.displayName,
							...(selectedOrg
								? {
										org_id: selectedOrg.organization.uid,
										org_name: selectedOrg.organization.name,
										org_role: selectedOrg.role,
									}
								: {
										org_id: "General",
										org_name: "General",
										org_role: "General",
									}),
						};
						event.$set = personProperties;

						return event;
					},
				});
			} catch (error) {
				console.error("PostHogProvider: Initialization failed", error);
				Sentry.captureException(error);
			}
		};

		initPostHog();
	}, []);

	return <PostHogContext.Provider value={{ posthog }}>{children}</PostHogContext.Provider>;
};
